import { isEqual } from "lodash";
import { IDispenseRequest } from "../../../@types/dispense";
import { ProductType } from "../../../@types/product";

function isBatchAwareLine(item: Record<string, unknown>): boolean {
  const t = String(item.type ?? "");
  return t === ProductType.CONTACT_LENSES || t === ProductType.SOLUTION;
}

export type DispenseLineSnapshot = {
  productId: string;
  quantity: number;
  packSize?: number;
  quantityIssued?: number;
  goodsInId?: string;
};

export type DispenseChangeSnapshot = {
  patientId: string | number | null;
  type: string;
  specType: string;
  pdRight: number | null;
  pdLeft: number | null;
  heightRight: number | null;
  heightLeft: number | null;
  bvd: number | null;
  pantoscopicTilt: number | null;
  wrapAngle: number | null;
  orderingNotes: string;
  handoverNotes: string;
  hobbies: string;
  isVDU: boolean;
  isSmoker: boolean;
  isDriver: boolean;
  isExternal: boolean;
  sourceType?: string;
  sourceId?: number;
  pickupDateTime: string;
  manualDiscount: number;
  framesAndLenses: DispenseLineSnapshot[];
  addonIds: number[];
  voucherIds: number[];
};

function toNum(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function buildPickupDateTimeIso(
  date: Date | null,
  time: string | null,
): string {
  if (!date || !time) return "";
  return new Date(
    `${date.toISOString().split("T")[0]}T${time}:00`,
  ).toISOString();
}

function normalizeFrameLines(
  framesAndLenses: Record<string, unknown>[],
): DispenseLineSnapshot[] {
  return framesAndLenses
    .map((item) => {
      const quantity = Math.max(1, Number(item.quantity) || 1);
      const isLotTracked = isBatchAwareLine(item);
      const packSize = isLotTracked
        ? Math.max(1, Math.floor(Number(item.packSize ?? 1) || 1))
        : undefined;
      const quantityIssued = isLotTracked
        ? Math.max(
            1,
            Math.floor(Number(item.quantityIssued ?? quantity) || 1),
          )
        : undefined;
      return {
        productId: String(item.id ?? ""),
        quantity: isLotTracked ? packSize! * quantityIssued! : quantity,
        packSize,
        quantityIssued,
        goodsInId: item.goodsInId
          ? String(item.goodsInId).trim() || undefined
          : undefined,
      };
    })
    .filter((row) => !!row.productId)
    .sort((a, b) => a.productId.localeCompare(b.productId));
}

export type DispenseSnapshotInput = Pick<
  IDispenseRequest,
  | "patientId"
  | "type"
  | "specType"
  | "pdRight"
  | "pdLeft"
  | "heightRight"
  | "heightLeft"
  | "bvd"
  | "pantoscopicTilt"
  | "wrapAngle"
  | "orderingNotes"
  | "handoverNotes"
  | "hobbies"
  | "isVDU"
  | "isSmoker"
  | "isDriver"
  | "isExternal"
  | "sourceType"
  | "sourceId"
  | "framesAndLenses"
  | "addons"
  | "vouchers"
>;

export const EMPTY_DISPENSE_SNAPSHOT_INPUT: DispenseSnapshotInput = {
  patientId: null,
  type: "",
  specType: "",
  pdRight: null,
  pdLeft: null,
  heightRight: null,
  heightLeft: null,
  bvd: null,
  pantoscopicTilt: null,
  wrapAngle: null,
  framesAndLenses: [],
  addons: [],
  vouchers: [],
  orderingNotes: "",
  handoverNotes: "",
  hobbies: "",
  isVDU: false,
  isSmoker: false,
  isDriver: false,
  isExternal: false,
};

/** Normalized save-relevant state for change detection (revert-friendly). */
export function buildDispenseChangeSnapshot(
  data: DispenseSnapshotInput,
  date: Date | null,
  time: string | null,
  manualDiscount: number,
): DispenseChangeSnapshot {
  const addonIds = (data.addons ?? [])
    .map((item: { addon?: unknown }) => Number(item.addon))
    .filter((id: number) => !Number.isNaN(id) && id !== 0)
    .sort((a: number, b: number) => a - b);

  const voucherIds = (data.vouchers ?? [])
    .map((item: { voucher?: unknown }) => Number(item.voucher))
    .filter((id: number) => !Number.isNaN(id) && id !== 0)
    .sort((a: number, b: number) => a - b);

  const frames = Array.isArray(data.framesAndLenses)
    ? (data.framesAndLenses as Record<string, unknown>[])
    : [];

  return {
    patientId: data.patientId ?? null,
    type: String(data.type ?? ""),
    specType: String(data.specType ?? ""),
    pdRight: toNum(data.pdRight),
    pdLeft: toNum(data.pdLeft),
    heightRight: toNum(data.heightRight),
    heightLeft: toNum(data.heightLeft),
    bvd: toNum(data.bvd),
    pantoscopicTilt: toNum(data.pantoscopicTilt),
    wrapAngle: toNum(data.wrapAngle),
    orderingNotes: String(data.orderingNotes ?? "").trim(),
    handoverNotes: String(data.handoverNotes ?? "").trim(),
    hobbies: String(data.hobbies ?? "").trim(),
    isVDU: Boolean(data.isVDU),
    isSmoker: Boolean(data.isSmoker),
    isDriver: Boolean(data.isDriver),
    isExternal: Boolean(data.isExternal),
    sourceType: data.sourceType || undefined,
    sourceId: data.sourceId ?? undefined,
    pickupDateTime: buildPickupDateTimeIso(date, time),
    manualDiscount: roundMoney(manualDiscount),
    framesAndLenses: normalizeFrameLines(frames),
    addonIds,
    voucherIds,
  };
}

export function dispenseSnapshotsEqual(
  a: DispenseChangeSnapshot,
  b: DispenseChangeSnapshot,
): boolean {
  return isEqual(a, b);
}
